import React, { useReducer } from 'react';
import { cloneDeep } from 'lodash';
import axios from 'axios';
import queryString from 'query-string';
import { ParsedListing, NeedDTO, OrgAddress } from '@bb/domain/dist/types';
import { ListBrokerageDTO } from '@bb/domain/dist/types/dto/ListingController/listBrokerage';
import { useParams, useLocation, Redirect } from 'react-router-dom';
import errorReporter from '@app/errorReporter';
import { useBreakpoint, useDebounce } from 'hooks';
import { LayoutContext } from 'contexts';
import { message } from 'components';
import { FOOTER_HEIGHT } from 'components/ListingTable/BasicListingTable';
import {
  JoinedSearchOptions,
  LocationsDTO,
  OpenHousesDTO,
  OpenHouse,
  SorterType,
} from './types';

import {
  SHOW_WELCOME_URL_PARAM as WELCOME_MODAL_PARAM,
  SUBMITTER_USER_ID_PARAM,
} from '../../onboarding/constants';

type QueryData = {
  searchTerm: string;
  searchOptions: JoinedSearchOptions;
  sortData: SorterType;
  pageNumber: number;
  type: 'listings' | 'openHouses' | 'havesWants';
};

type ParamsType = {
  address?: string;
  searchType?: string;
  type?: string;
  sort_field?: string;
  sort_asc?: string;
  minPrice?: number;
  maxPrice?: number;
  status?: string;
  location?: string | number | 'All';
  locationAddress?: number | 'All';
  avail?: string;
  agent?: string;
  // for Haves/Wants
  havesWantsLocation?: string | 'All';
  subject?: string;
};

type FilteredLocation = {
  name: string;
  isDeleted?: boolean;
  idx: number;
  addresses: (OrgAddress & { idx: number })[];
};

type MyBrokerageContextType = {
  loading: boolean;
  queryData: QueryData;
  initialQueryData: QueryData;
  setQueryData: React.Dispatch<React.SetStateAction<QueryData>>;
  setRedirect: React.Dispatch<React.SetStateAction<string>>;
  results: {
    count: number;
    listings: ParsedListing[];
    openHouses: OpenHouse[];
    havesWants: NeedDTO[];
  };
  sort: (key: string) => void;
  selectedRowKeys: React.ReactText[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.ReactText[]>>;
  locations: FilteredLocation[];
  isAdvancedSearchOpen: boolean;
  setAdvancedSearchOpen: React.Dispatch<React.SetStateAction<boolean>>;
  advancedSearchHeight: number;
  filtersLocked: boolean;
  clearSearchOptions: () => void;
  setLockedSearchOptions: (
    searchOptions: QueryData['searchOptions'] | null,
  ) => void;
  getFormattedSortData: () => {
    sort_field: string;
    sort_asc: string;
  };
  showOpenHouses: null | boolean;
  heightOffset: number;
};

const initialQueryData: QueryData = {
  searchTerm: '',
  searchOptions: {
    minPrice: undefined,
    maxPrice: undefined,
    status: 'All',
    location: 'All',
    locationAddress: 'All',
    avail: 'All',
    agent: '',
    subject: 'All',
    havesWantsLocation: 'All',
  },
  sortData: {
    field: '',
    order: undefined,
  },
  pageNumber: 0,
  type: 'listings',
};
const LOCAL_STORAGE_FILTERS_LOCK_KEY = 'MyBrokerage_Filters_Lock';

const MyBrokerageContext = React.createContext<MyBrokerageContextType>(null!);

export const MyBrokerageContextProvider: React.FC = ({ children }) => {
  const location = useLocation();
  const params = useParams<{ pageNumber: string }>();
  const { md, xl } = useBreakpoint();
  const [loading, setLoading] = React.useState(true);
  const [redirect, setRedirect] = React.useState('');
  const { setAppTitle } = React.useContext(LayoutContext);
  const [showOpenHouses, setShowOpenHouses] =
    React.useState<MyBrokerageContextType['showOpenHouses']>(null);

  const [isAdvancedSearchOpen, setAdvancedSearchOpen] = React.useState(false);

  const [lockedSearchOptions, setLockedSearchOptions] = useReducer(
    (
      _: QueryData['searchOptions'],
      newValue: QueryData['searchOptions'] | null,
    ) => {
      localStorage.setItem(
        LOCAL_STORAGE_FILTERS_LOCK_KEY,
        JSON.stringify(newValue ?? ''),
      );

      return newValue;
    },
    JSON.parse(localStorage.getItem(LOCAL_STORAGE_FILTERS_LOCK_KEY) || null!),
  );

  const filtersLocked = Boolean(lockedSearchOptions);

  const parseDataFromUrl = (): QueryData => {
    const parsed = queryString.parse(location.search) as Record<string, string>;
    const initType = () => {
      const { pathname } = location;
      let type: QueryData['type'] = 'listings';
      if (pathname.search('open_houses') > -1) {
        type = 'openHouses';
      } else if (pathname.search('needs') > -1) {
        type = 'havesWants';
      }
      return type;
    };
    const initSearchTerm = () => {
      const type = initType();
      const { address, note } = parsed;
      if (type === 'listings') return address || '';
      if (type === 'openHouses') return '';
      return note || '';
    };
    const initSortData = () => {
      const { sort_field, sort_asc } = parsed;
      if (!sort_field) return initialQueryData.sortData;
      const field = sort_field === 'listedAt' ? 'dom' : sort_field;
      return {
        field,
        order: sort_asc === 'true' ? 'ascend' : 'descend',
      } as QueryData['sortData'];
    };

    const initSearchOptions = () => {
      const type = initType();
      if (lockedSearchOptions) return lockedSearchOptions;

      const {
        minPrice,
        maxPrice,
        location: parsedLocation,
        locationAddress: parsedLocationAddress,
      } = parsed;
      const result: JoinedSearchOptions = { ...initialQueryData.searchOptions };
      const minPriceNumber = Number(minPrice);
      const maxPriceNumber = Number(maxPrice);
      if (!Number.isNaN(minPriceNumber)) result.minPrice = minPriceNumber;
      if (!Number.isNaN(maxPriceNumber)) result.maxPrice = maxPriceNumber;
      if (parsedLocation) {
        if (type === 'havesWants') {
          result.havesWantsLocation = parsedLocation;
        } else {
          const locationNumber = Number(parsedLocation);
          const formattedLocation = Number.isNaN(locationNumber)
            ? 'All'
            : locationNumber;
          result.location = formattedLocation;
        }
      }
      if (parsedLocationAddress) {
        const locationAddressNumber = Number(parsedLocationAddress);
        const formattedLocationAddress = Number.isNaN(locationAddressNumber)
          ? 'All'
          : locationAddressNumber;
        result.locationAddress = formattedLocationAddress;
      }
      [
        'status',
        'avail',
        'agent',
        'subject',
        WELCOME_MODAL_PARAM,
        SUBMITTER_USER_ID_PARAM,
      ].forEach((key: keyof JoinedSearchOptions) => {
        if (parsed[key]) {
          (result as any)[key] = parsed[key];
        }
      });

      return result;
    };

    return {
      searchTerm: initSearchTerm(),
      searchOptions: initSearchOptions(),
      sortData: initSortData(),
      type: initType(),
      pageNumber: Number(params.pageNumber),
    };
  };

  const [queryData, setQueryData] = React.useState(parseDataFromUrl());

  const getFormattedSortData = () => {
    const { field, order } = queryData.sortData;
    let sort_field = field.toString();
    if (field === 'dom') sort_field = 'listedAt';

    return {
      sort_field,
      sort_asc: (order === 'ascend').toString(),
    };
  };

  const addParamsToUrl = (newParams: ParamsType) => {
    const paramsForUrl = Object.keys(newParams)
      .filter((key: keyof ParamsType) => {
        if (key === 'havesWantsLocation') return false;
        return newParams[key] === 0 || newParams[key];
      })
      .map((key: keyof ParamsType) => `${key}=${newParams[key]}`)
      .join('&');
    setRedirect(`${location.pathname}?${paramsForUrl}`);
  };

  const [selectedRowKeys, setSelectedRowKeys] = React.useState([]);
  const advancedSearchHeight = React.useMemo(() => 120 + (md ? 0 : 115), [md]);

  const debouncedQuery = useDebounce(queryData, 200);
  const [results, setResults] = React.useState<
    MyBrokerageContextType['results']
  >({
    count: 0,
    listings: [],
    openHouses: [],
    havesWants: [],
  });
  const [locations, setLocations] = React.useState<
    MyBrokerageContextType['locations']
  >([]);

  const heightOffset = React.useMemo(() => {
    const { type: tab } = queryData;
    let result = 247;

    if (tab === 'havesWants') result += xl ? 0 : 10;
    if (tab === 'listings') {
      result -= md ? 0 : 78;
      if (md && isAdvancedSearchOpen) result += advancedSearchHeight;
    }

    result += FOOTER_HEIGHT;
    return result;
  }, [advancedSearchHeight, isAdvancedSearchOpen, md, queryData, xl]);

  const filterDeletedAddresses = (
    acc: FilteredLocation['addresses'],
    _address: OrgAddress & { idx: number },
    idx: number,
  ) => {
    if (!_address.isDeleted) acc.push({ ..._address, idx });
    return acc;
  };

  const filterDeletedLocations = (
    acc: FilteredLocation[],
    _location: FilteredLocation,
    idx: number,
  ) => {
    if (!_location.isDeleted)
      acc.push({
        ..._location,
        idx,
        addresses: _location.addresses.reduce(filterDeletedAddresses, []),
      });
    return acc;
  };

  const getLocations = async () => {
    try {
      const {
        data: { locations: _locations },
      } = await axios.get<LocationsDTO>('/org/locations');
      const activeLocations = _locations.reduce(filterDeletedLocations, []);
      setLocations(activeLocations);
    } catch (err) {
      errorReporter.error(err);
    }
  };

  const changeLocation = () => {
    const { type } = queryData;
    let path = '/';
    let appTitle = '';
    if (type === 'listings') {
      path = `/listing/list/brokerage/${queryData.pageNumber}`;
      appTitle = 'My Brokerage';
    } else if (type === 'havesWants') {
      path = `/needs/${queryData.pageNumber}`;
      appTitle = 'Haves / Wants';
    } else if (type === 'openHouses') {
      path = `/open_houses`;
      appTitle = 'Open Houses';
    }
    const { search } = location;
    setRedirect(`${path}${search}`);
    setAppTitle(appTitle);
  };

  const runSearch = () => {
    const runListingsSearch = async () => {
      const newParams = {
        ...queryData.searchOptions,
        ...getFormattedSortData(),
        address: queryData.searchTerm,
        searchType: 'brokerage',
        type: 'brokerage',
      };

      const {
        data: { listings, count },
      } = await axios.get<ListBrokerageDTO>(
        `/listing/brokerage/${queryData.pageNumber}`,
        {
          params: newParams,
        },
      );
      const listingIds = listings.map(listing => listing.id).join(',');
      window.sessionStorage.setItem('listings', listingIds);

      addParamsToUrl(newParams);
      setResults(prevResults => ({ ...prevResults, listings, count }));
      setLoading(false);
    };

    const runOpenHousesSearch = async () => {
      const {
        data: { listings: openHouses, count },
      } = await axios.get<OpenHousesDTO>(`/listing/open_houses`);
      setResults(prevResults => ({ ...prevResults, openHouses, count }));
      setLoading(false);
    };

    const runHavesWantsSearch = async () => {
      const { havesWantsLocation = 'All', subject = 'All' } =
        queryData.searchOptions;

      const newParams = {
        pageNumber: queryData.pageNumber,
        location: havesWantsLocation,
        subject,
        note: queryData.searchTerm,
      };
      const {
        data: { data: havesWants, count },
      } = await axios.get<{ data: NeedDTO[]; count: number }>(`/need/`, {
        params: newParams,
      });

      addParamsToUrl(newParams);
      setResults(prevResults => ({ ...prevResults, havesWants, count }));
      setLoading(false);
    };

    try {
      const { type } = queryData;
      if (type === 'listings') runListingsSearch();
      if (type === 'openHouses') runOpenHousesSearch();
      if (type === 'havesWants') runHavesWantsSearch();
    } catch (err) {
      errorReporter.error(err);
      message.error('Loading Failed');
    }
  };

  const clearSearchOptions = () => {
    setQueryData(cloneDeep(initialQueryData));
    setLockedSearchOptions(null);
  };

  const sort: MyBrokerageContextType['sort'] = key => {
    const { sortData } = queryData;
    const isBeingSorted = key === sortData.field;
    let order: SorterType['order'] = 'ascend';

    if (isBeingSorted) {
      if (sortData.order === 'ascend') {
        order = 'descend';
      } else {
        order = 'ascend';
      }
    }

    const newSortData: SorterType = {
      field: key,
      order,
    };

    setQueryData(prev => ({ ...prev, sortData: newSortData }));
  };

  const getOrgSettings = async () => {
    const { data } = await axios.get<{ enableOpenHouses: boolean }>(
      '/org/settings',
    );
    setShowOpenHouses(data.enableOpenHouses);
  };

  React.useEffect(() => {
    setLoading(true);
    changeLocation();
    if (md) setAdvancedSearchOpen(false);
  }, [queryData]);

  React.useEffect(() => {
    runSearch();
  }, [debouncedQuery]);

  React.useEffect(() => {
    // Starts as null. False means the org setting was fetched but open houses are not enabled.
    if (showOpenHouses === false && queryData.type === 'openHouses') {
      setQueryData(prev => ({ ...prev, type: 'listings' }));
    }
  }, [showOpenHouses]);

  React.useEffect(() => {
    getLocations();
    getOrgSettings();
  }, []);

  React.useEffect(() => {
    if (!md) setSelectedRowKeys([]);
  }, [md]);

  return (
    <MyBrokerageContext.Provider
      value={{
        loading,
        initialQueryData,
        queryData,
        setQueryData,
        results,
        sort,
        getFormattedSortData,
        showOpenHouses,
        advancedSearchHeight,
        isAdvancedSearchOpen,
        setAdvancedSearchOpen,
        filtersLocked,
        setLockedSearchOptions,
        clearSearchOptions,
        setRedirect,
        selectedRowKeys,
        setSelectedRowKeys,
        locations,
        heightOffset,
      }}
    >
      {redirect && <Redirect push to={redirect} />}
      {children}
    </MyBrokerageContext.Provider>
  );
};

export default MyBrokerageContext;
